import apiClient from "./client";

export type FeedbackPlatform = "web" | "ios" | "android";

export interface FeedbackPayload {
  /** Free-text feedback. Capped at 5k by the backend serializer. */
  body: string;
  /** Full URL of the page the feedback was sent from (clickable in the QA portal). */
  page_url: string;
  platform: FeedbackPlatform;
  /** CSS selector of the element the feedback is about (element-pick mode). */
  element_selector?: string;
  /**
   * How many elements that selector matched at pick time. >1 means it is
   * ambiguous, and the ticket footer says so — a selector shown to a reader must
   * not imply precision it does not have.
   */
  element_selector_matches?: number;
  /**
   * Human-readable name for that element (its accessible name or visible text).
   * The QA portal never renders `element_selector`, so this is the part that
   * actually reaches a reader — the backend folds it into the forwarded body.
   */
  element_label?: string;
  /** Viewport at submit time, so a layout complaint is reproducible at its width. */
  viewport_width?: number;
  viewport_height?: number;
  /**
   * Self-declared "filed by" name, remembered locally so it is typed once.
   * NOT identity — the backend keeps it in its own column and always renders it
   * with a "(self-declared)" qualifier. Exists because three reviewers share one
   * demo login, so every ticket otherwise reads as the same person.
   */
  filer_name?: string;
  /** Base64 `data:image/jpeg` screenshot. Backend caps at 4 MiB / 5 MiB request. */
  screenshot?: string;
}

export interface FeedbackResponse {
  qa_log_id: string | null;
}

/**
 * Forward in-app feedback to the QA webhook via the backend proxy. The
 * QA_WEBHOOK_TOKEN lives server-side (POST /api/feedback/) — the client never
 * sees it and only sends body / page_url / platform / element_selector /
 * screenshot. Works pre-auth (the widget renders before login); if a Clerk token
 * is present the apiClient interceptor attaches it so the backend records the visitor.
 */
export async function sendFeedback(payload: FeedbackPayload): Promise<FeedbackResponse> {
  const { data } = await apiClient.post<FeedbackResponse>("/feedback/", payload);
  return data;
}
